// @ts-nocheck
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calendar, Clock, MapPin, Users, AlertTriangle, UserCheck, List, Grid, Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TournamentScheduler, SchedulingConflict } from "@/utils/tournamentScheduler";
import { useToast } from "@/hooks/use-toast";
import { RefereeAssignmentDialog } from "./RefereeAssignmentDialog";
import ScheduleListView from "./ScheduleListView";
import ScheduleGridView from "./ScheduleGridView";
import FinalizePoolPlayButton from "./FinalizePoolPlayButton";
import TournamentNotificationDialog from "./TournamentNotificationDialog";
import { parseLocalDate } from '@/utils/formatters';
import { formatTimeRange } from '@/lib/dates';

interface TournamentSchedulingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: any;
}

interface TimeSlotForm {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  matchDurationMinutes: number;
  breakMinutes: number;
}

interface VenueCourtForm {
  courtName: string;
  notes: string;
}

interface RefereeForm {
  name: string;
  email: string;
  phone: string;
  certificationLevel: string;
}

export function TournamentSchedulingDialog({
  isOpen,
  onClose,
  tournament
}: TournamentSchedulingDialogProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [scheduler, setScheduler] = useState<TournamentScheduler | null>(null);
  const [conflicts, setConflicts] = useState<SchedulingConflict[]>([]);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [courts, setCourts] = useState<any[]>([]);
  const [eventId, setEventId] = useState<string | null>(null);
  const [referees, setReferees] = useState<any[]>([]);
  const [matches, setMatches] = useState<any[]>([]);
  const [showRefereeAssignment, setShowRefereeAssignment] = useState(false);
  const [scheduleView, setScheduleView] = useState<'list' | 'grid'>('list');
  const [eventDates, setEventDates] = useState<string[]>([]);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);

  const [timeSlotForm, setTimeSlotForm] = useState<TimeSlotForm>({
    startDate: '',
    endDate: '',
    startTime: '08:00',
    endTime: '18:00',
    matchDurationMinutes: 60,
    breakMinutes: 15
  });

  const [courtForm, setCourtForm] = useState<VenueCourtForm>({
    courtName: '',
    notes: ''
  });

  const [refereeForm, setRefereeForm] = useState<RefereeForm>({
    name: '',
    email: '',
    phone: '',
    certificationLevel: ''
  });

  useEffect(() => {
    if (isOpen && tournament?.id) {
      const newScheduler = new TournamentScheduler(tournament.id);
      setScheduler(newScheduler);
      loadData();
      fetchEventDates();
    }
  }, [isOpen, tournament?.id]);

  const fetchEventDates = async () => {
    if (!tournament?.id) return;
    
    const { data: tournamentData } = await supabase
      .from('tournaments')
      .select('events(start_date, end_date)')
      .eq('id', tournament.id)
      .single();

    if (tournamentData?.events) {
      const event = tournamentData.events as any;
      const start = parseLocalDate(event.start_date);
      const end = parseLocalDate(event.end_date || event.start_date);
      const dates: string[] = [];
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
      }
      
      setEventDates(dates);
    }
  };

  const loadData = async () => {
    if (!tournament?.id) return;

    try {
      // First get the tournament's event id and venue_id
      const { data: tournamentData } = await supabase
        .from('tournaments')
        .select('event_id, events(venue_id)')
        .eq('id', tournament.id)
        .single();

      const currentEventId = tournamentData?.event_id || tournament?.event_id;
      const eventVenueId = (tournamentData?.events as any)?.venue_id;
      setEventId(currentEventId || null);

      // Fetch courts scoped to this event
      const courtsQuery = supabase
        .from('event_courts')
        .select('*')
        .eq('event_id', currentEventId)
        .eq('is_active', true)
        .order('sort_order');

      const [timeSlotsResult, courtsResult, refereesResult, matchesResult] = await Promise.all([
        supabase.from('tournament_time_slots').select('*').eq('tournament_id', tournament.id),
        courtsQuery,
        supabase.from('tournament_referees').select('*').eq('tournament_id', tournament.id),
        supabase.from('tournament_matches').select('*').eq('tournament_id', tournament.id).order('round_number')
      ]);

      setTimeSlots(timeSlotsResult.data || []);
      setCourts(courtsResult.data || []);
      setReferees(refereesResult.data || []);
      setMatches(matchesResult.data || []);
      
      console.log('[TournamentSchedulingDialog] Loaded courts for venue:', eventVenueId, courtsResult.data?.length, 'courts');
    } catch (error) {
      console.error('Error loading scheduling data:', error);
      toast({
        title: "Error",
        description: "Failed to load scheduling data",
        variant: "destructive"
      });
    }
  };

  const handleCreateTimeSlots = async () => {
    if (!scheduler || !timeSlotForm.startDate || !timeSlotForm.endDate) return;

    setIsLoading(true);
    try {
      await scheduler.createTimeSlots(
        new Date(timeSlotForm.startDate),
        new Date(timeSlotForm.endDate),
        timeSlotForm.startTime,
        timeSlotForm.endTime,
        timeSlotForm.matchDurationMinutes,
        timeSlotForm.breakMinutes
      );

      await loadData();
      toast({
        title: "Success",
        description: "Time slots created successfully"
      });
    } catch (error) {
      console.error('Error creating time slots:', error);
      toast({
        title: "Error",
        description: "Failed to create time slots",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCourt = async () => {
    if (!eventId || !courtForm.courtName) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('event_courts')
        .insert({
          event_id: eventId,
          name: courtForm.courtName,
          is_active: true,
          sort_order: courts.length,
          notes: courtForm.notes || null
        });

      if (error) throw error;

      await loadData();
      setCourtForm({ courtName: '', notes: '' });
      toast({
        title: "Success",
        description: "Court added successfully"
      });
    } catch (error) {
      console.error('Error adding court:', error);
      toast({
        title: "Error",
        description: "Failed to add court",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddReferee = async () => {
    if (!refereeForm.name) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('tournament_referees')
        .insert({
          tournament_id: tournament.id,
          name: refereeForm.name,
          email: refereeForm.email || null,
          phone: refereeForm.phone || null,
          certification_level: refereeForm.certificationLevel || null
        });

      if (error) throw error;

      await loadData();
      setRefereeForm({ name: '', email: '', phone: '', certificationLevel: '' });
      toast({
        title: "Success",
        description: "Referee added successfully"
      });
    } catch (error) {
      console.error('Error adding referee:', error);
      toast({
        title: "Error",
        description: "Failed to add referee",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearTimeSlots = async () => {
    if (!tournament?.id) return;
    
    const confirmed = window.confirm('Are you sure you want to delete all time slots? This cannot be undone.');
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('tournament_time_slots')
        .delete()
        .eq('tournament_id', tournament.id);

      if (error) throw error;

      await loadData();
      toast({
        title: "Success",
        description: "All time slots cleared"
      });
    } catch (error) {
      console.error('Error clearing time slots:', error);
      toast({
        title: "Error",
        description: "Failed to clear time slots",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutoSchedule = async () => {
    if (!scheduler) return;

    setIsLoading(true);
    try {
      await scheduler.loadData();
      const schedulingConflicts = await scheduler.autoSchedule();
      
      setConflicts(schedulingConflicts);
      await loadData();

      if (schedulingConflicts.length === 0) {
        toast({
          title: "Success",
          description: "Tournament scheduled successfully with no conflicts"
        });
      } else {
        toast({
          title: "Partial Success",
          description: `Tournament scheduled with ${schedulingConflicts.length} conflicts to resolve`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error auto-scheduling:', error);
      toast({
        title: "Error",
        description: "Failed to auto-schedule tournament",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearSchedule = async () => {
    if (!scheduler) return;
    
    const confirmed = window.confirm('Are you sure you want to clear the schedule? This will remove all scheduled times and courts from matches.');
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await scheduler.clearSchedule();
      setConflicts([]);
      await loadData();
      
      toast({
        title: "Success",
        description: "Schedule cleared. You can now re-run Auto-Schedule."
      });
    } catch (error) {
      console.error('Error clearing schedule:', error);
      toast({
        title: "Error",
        description: "Failed to clear schedule",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const scheduledMatchCount = matches.filter(m => m.scheduled_time).length;
  const unscheduledMatches = matches.filter(m => !m.scheduled_time);
  
  // Pool play phase awareness
  const poolMatches = matches.filter(m => m.bracket_type === 'pool').length;
  const playoffMatches = matches.filter(m => m.bracket_type === 'winners' || m.bracket_type === 'final' || m.bracket_type === 'losers').length;
  const schedulableCount = matches.filter(m => m.team1_id && m.team2_id && !m.scheduled_time).length;
  const tbdCount = matches.filter(m => !m.team1_id || !m.team2_id).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tournament Scheduling - {tournament?.name}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="timeslots">Time Slots</TabsTrigger>
            <TabsTrigger value="courts">Courts</TabsTrigger>
            <TabsTrigger value="referees">Referees</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {/* Match Phase Breakdown */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-green-600">{poolMatches}</div>
                  <div className="text-sm text-muted-foreground">Pool Play (Ready)</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-yellow-600">{playoffMatches}</div>
                  <div className="text-sm text-muted-foreground">Playoffs (TBD Teams)</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-primary">{scheduledMatchCount}</div>
                  <div className="text-sm text-muted-foreground">Scheduled</div>
                </CardContent>
              </Card>
            </div>

            {/* Pool Play + Playoffs Info Message */}
            {playoffMatches > 0 && tbdCount > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Pool Play + Playoffs:</strong> Only pool play matches ({poolMatches}) can be scheduled now. 
                  Playoff matches ({playoffMatches}) will be scheduled after pool play is complete and teams advance.
                </p>
              </div>
            )}

            {/* Resources Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Time Slots</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{timeSlots.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Courts</CardTitle>
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{courts.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Referees</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{referees.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Matches</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {scheduledMatchCount}/{matches.length}
                  </div>
                </CardContent>
              </Card>
            </div>

            {conflicts.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {conflicts.length} scheduling conflicts detected. Review the Schedule tab for details.
                </AlertDescription>
              </Alert>
            )}

            {/* Finalize Pool Play Button */}
            {tournament?.tournament_type === 'pool_play_playoffs' && poolMatches > 0 && (
              <div className="flex justify-center">
                <FinalizePoolPlayButton 
                  tournamentId={tournament.id}
                  onFinalized={() => {
                    loadData();
                    toast({
                      title: "Pool Play Finalized",
                      description: "Playoff teams have been seeded. You can now schedule playoff matches."
                    });
                  }}
                />
              </div>
            )}

            <div className="flex justify-center gap-3 flex-wrap">
              <Button 
                onClick={handleAutoSchedule}
                disabled={isLoading || timeSlots.length === 0 || courts.length === 0 || schedulableCount === 0}
                size="lg"
              >
                {isLoading 
                  ? "Scheduling..." 
                  : schedulableCount > 0 
                    ? `Auto-Schedule ${schedulableCount} Matches`
                    : 'All Available Matches Scheduled'
                }
              </Button>
              {scheduledMatchCount > 0 && (
                <Button 
                  onClick={handleClearSchedule}
                  disabled={isLoading}
                  size="lg"
                  variant="outline"
                >
                  Clear Schedule
                </Button>
              )}
              {scheduledMatchCount > 0 && (
                <Button 
                  onClick={() => setShowNotifyDialog(true)}
                  size="lg"
                  variant="secondary"
                  className="border-green-600 text-green-700 hover:bg-green-50"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Teams
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="timeslots" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Create Time Slots</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={timeSlotForm.startDate}
                      onChange={(e) => setTimeSlotForm(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={timeSlotForm.endDate}
                      onChange={(e) => setTimeSlotForm(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="startTime">Daily Start Time</Label>
                    <Input
                      id="startTime"
                      type="time"
                      value={timeSlotForm.startTime}
                      onChange={(e) => setTimeSlotForm(prev => ({ ...prev, startTime: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="endTime">Daily End Time</Label>
                    <Input
                      id="endTime"
                      type="time"
                      value={timeSlotForm.endTime}
                      onChange={(e) => setTimeSlotForm(prev => ({ ...prev, endTime: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="matchDuration">Match Duration (minutes)</Label>
                    <Input
                      id="matchDuration"
                      type="number"
                      value={timeSlotForm.matchDurationMinutes}
                      onChange={(e) => setTimeSlotForm(prev => ({ ...prev, matchDurationMinutes: parseInt(e.target.value) }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="breakMinutes">Break Between Matches (minutes)</Label>
                    <Input
                      id="breakMinutes"
                      type="number"
                      value={timeSlotForm.breakMinutes}
                      onChange={(e) => setTimeSlotForm(prev => ({ ...prev, breakMinutes: parseInt(e.target.value) }))}
                    />
                  </div>
                </div>
                <Button onClick={handleCreateTimeSlots} disabled={isLoading}>
                  {isLoading ? "Creating..." : "Create Time Slots"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Existing Time Slots ({timeSlots.length})</CardTitle>
                {timeSlots.length > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={handleClearTimeSlots}
                    disabled={isLoading}
                  >
                    Clear All
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {timeSlots.map((slot) => (
                    <div key={slot.id} className="flex justify-between items-center p-2 border rounded">
                      <span>
                        {new Date(slot.start_time).toLocaleDateString()} - {' '}
                        {formatTimeRange(slot.start_time, slot.end_time)}
                      </span>
                      <Badge variant="outline">{slot.duration_minutes}min</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="courts" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Add Court</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="courtName">Court Name</Label>
                    <Input
                      id="courtName"
                      placeholder="Court 1, Field A, etc."
                      value={courtForm.courtName}
                      onChange={(e) => setCourtForm(prev => ({ ...prev, courtName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="courtNotes">Notes</Label>
                    <Input
                      id="courtNotes"
                      placeholder="Optional notes"
                      value={courtForm.notes}
                      onChange={(e) => setCourtForm(prev => ({ ...prev, notes: e.target.value }))}
                    />
                  </div>
                </div>
                <Button onClick={handleAddCourt} disabled={isLoading}>
                  {isLoading ? "Adding..." : "Add Court"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Available Courts ({courts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {courts.map((court) => (
                    <div key={court.id} className="flex justify-between items-center p-2 border rounded">
                      <span>{court.name}</span>
                      {court.notes && <Badge variant="outline">{court.notes}</Badge>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referees" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Add Referee</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="refereeName">Name</Label>
                    <Input
                      id="refereeName"
                      value={refereeForm.name}
                      onChange={(e) => setRefereeForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="refereeEmail">Email</Label>
                    <Input
                      id="refereeEmail"
                      type="email"
                      value={refereeForm.email}
                      onChange={(e) => setRefereeForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="refereePhone">Phone</Label>
                    <Input
                      id="refereePhone"
                      value={refereeForm.phone}
                      onChange={(e) => setRefereeForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="certification">Certification Level</Label>
                    <Input
                      id="certification"
                      value={refereeForm.certificationLevel}
                      onChange={(e) => setRefereeForm(prev => ({ ...prev, certificationLevel: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddReferee} disabled={isLoading}>
                    {isLoading ? "Adding..." : "Add Referee"}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowRefereeAssignment(true)}
                    disabled={referees.length === 0}
                  >
                    <UserCheck className="w-4 h-4 mr-2" />
                    Assign to Matches
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tournament Referees ({referees.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {referees.map((referee) => (
                    <div key={referee.id} className="flex justify-between items-center p-2 border rounded">
                      <span>{referee.name}</span>
                      {referee.certification_level && <Badge variant="outline">{referee.certification_level}</Badge>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule" className="space-y-4">
            {conflicts.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <div className="font-medium">Scheduling Conflicts:</div>
                    {conflicts.map((conflict, index) => (
                      <div key={index} className="text-sm">
                        • {conflict.message} (Match ID: {conflict.matchId.slice(0, 8)})
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* View Toggle and Summary */}
            <div className="flex items-center justify-between">
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span>Scheduled: {scheduledMatchCount}</span>
                <span>Unscheduled: {unscheduledMatches.length}</span>
                <span>Total: {matches.length}</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={scheduleView === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setScheduleView('list')}
                >
                  <List className="w-4 h-4 mr-1" />
                  List
                </Button>
                <Button
                  variant={scheduleView === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setScheduleView('grid')}
                >
                  <Grid className="w-4 h-4 mr-1" />
                  Grid
                </Button>
              </div>
            </div>

            {/* Schedule View */}
            {scheduleView === 'list' ? (
              <ScheduleListView
                tournamentId={tournament?.id}
                courts={courts.map(c => ({ id: c.id, name: c.name }))}
                referees={referees}
              />
            ) : (
              <ScheduleGridView
                tournamentId={tournament?.id}
                courts={courts.map(c => ({ id: c.id, name: c.name }))}
                eventDates={eventDates}
              />
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Referee Assignment Dialog */}
      <RefereeAssignmentDialog
        isOpen={showRefereeAssignment}
        onClose={() => {
          setShowRefereeAssignment(false);
          loadData(); // Refresh data after assignments
        }}
        tournament={tournament}
      />

      {/* Tournament Notification Dialog */}
      <TournamentNotificationDialog
        tournamentId={tournament?.id || ''}
        eventId={tournament?.event_id || ''}
        organizationId={tournament?.events?.organization_id || ''}
        tournamentName={tournament?.name || tournament?.division_name || 'Tournament'}
        open={showNotifyDialog}
        onOpenChange={setShowNotifyDialog}
      />
    </Dialog>
  );
}